/* eslint-disable react/prop-types */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser'
import {
  useRemoveAutomaticCouponsMutation,
  useRemoveAutomaticTurboCouponsMutation,
} from '../hooks/useDiscountCouponQuery'
import RemoveAutomaticDialog from './RemoveAutomaticDialog'
import RemoveAutomaticTurboDialog from './RemoveAutomaticTurboDialog'

function CouponSection({ title, sx, addLabel, onAddClick, removeLabel, onRemoveClick }) {
  return (
    <Paper sx={sx} variant="outlined">
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <Button variant="outlined" onClick={onAddClick}>
          {addLabel}
        </Button>
        <Button variant="outlined" color="error" onClick={onRemoveClick}>
          {removeLabel}
        </Button>
      </Stack>
    </Paper>
  )
}

function buildRemoveHandler(mutation, closeDialog, successMessage, notify) {
  return function handleConfirmRemove(payload) {
    mutation.mutate(payload, {
      onSuccess: () => {
        closeDialog()
        notify(successMessage)
      },
      onError: (error) => notify(`Ocorreu um erro: ${error?.message || 'Erro desconhecido'}`),
    })
  }
}

function AutomaticCouponsTab() {
  const navigate = useNavigate()
  const { allowedToGenerateAutomaticCoupons } = useCurrentUser()
  const removeAutomaticMutation = useRemoveAutomaticCouponsMutation()
  const removeAutomaticTurboMutation = useRemoveAutomaticTurboCouponsMutation()
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [removeTurboDialogOpen, setRemoveTurboDialogOpen] = useState(false)

  function notify(message) {
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  const handleConfirmRemoveAutomaticCoupons = buildRemoveHandler(
    removeAutomaticMutation,
    () => setRemoveDialogOpen(false),
    'Cupons automáticos removidos com sucesso',
    notify,
  )

  const handleConfirmRemoveAutomaticTurboCoupons = buildRemoveHandler(
    removeAutomaticTurboMutation,
    () => setRemoveTurboDialogOpen(false),
    'Cupons automáticos de turbo removidos com sucesso',
    notify,
  )

  if (!allowedToGenerateAutomaticCoupons) {
    return null
  }

  return (
    <Box>
      <CouponSection
        title="Automáticos"
        sx={{ p: 3, mb: 2 }}
        addLabel="Adicionar Automáticos"
        onAddClick={() => navigate('/workspace/discount-coupons/add/automatic')}
        removeLabel="Remover Automáticos"
        onRemoveClick={() => setRemoveDialogOpen(true)}
      />

      <CouponSection
        title="Turbos"
        sx={{ p: 3 }}
        addLabel="Adicionar Turbo"
        onAddClick={() => navigate('/workspace/discount-coupons/add/automatic-turbos')}
        removeLabel="Remover Turbo"
        onRemoveClick={() => setRemoveTurboDialogOpen(true)}
      />

      <RemoveAutomaticDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={handleConfirmRemoveAutomaticCoupons}
        isPending={removeAutomaticMutation.isPending}
      />

      <RemoveAutomaticTurboDialog
        open={removeTurboDialogOpen}
        onClose={() => setRemoveTurboDialogOpen(false)}
        onConfirm={handleConfirmRemoveAutomaticTurboCoupons}
        isPending={removeAutomaticTurboMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AutomaticCouponsTab
